import React from 'react';
import { Badge } from './badge';
import { cn } from '../../lib/utils';

/**
 * Booking Status Badge component using shadcn/ui Badge
 * @param {Object} props - Component props
 * @param {string} props.status - Booking status (BOOKING_INITIALISED, PAYMENT_INITIATED, BOOKING_SUCCESS, BOOKING_FAILED, CANCELLED, REFUNDED)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showIcon - Whether to show status icon (default: false)
 * @returns {JSX.Element} BookingStatusBadge component
 */
const BookingStatusBadge = ({
  status,
  className = '',
  showIcon = false,
  ...props
}) => {
  // Get status configuration for actual API booking statuses
  const getStatusConfig = () => {
    switch (status?.toUpperCase()) {
      case 'BOOKING_INITIALISED':
      case 'INITIALISED':
        return {
          label: 'Initialised',
          variant: 'secondary',
          className: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200'
        };
      case 'PAYMENT_INITIATED':
        return {
          label: 'Payment Initiated',
          variant: 'default',
          className: 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200'
        };
      case 'BOOKING_SUCCESS':
        return {
          label: 'Success',
          variant: 'default',
          className: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200'
        };
      case 'BOOKING_FAILED':
        return {
          label: 'Failed',
          variant: 'destructive',
          className: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'
        };
      case 'BOOKING_ERROR':
        return {
          label: 'Error',
          variant: 'destructive',
          className: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'
        };
      case 'CANCELLED':
        return {
          label: 'Cancelled',
          variant: 'destructive',
          className: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'
        };
      case 'REFUNDED':
        return {
          label: 'Refunded',
          variant: 'outline',
          className: 'bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200'
        };
      // Legacy statuses for backward compatibility
      case 'CONFIRMED':
        return {
          label: 'Confirmed',
          variant: 'default',
          className: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200'
        };
      case 'PENDING':
        return {
          label: 'Pending',
          variant: 'secondary',
          className: 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200'
        };
      default:
        return {
          label: status || 'Unknown',
          variant: 'outline',
          className: 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Badge
      variant={config.variant}
      className={cn(
        'inline-flex items-center font-medium',
        config.className,
        className
      )}
      {...props}
    >
      {config.label}
    </Badge>
  );
};

export default BookingStatusBadge;
